import posthog from 'posthog-js';
import type { PostHogConfig } from 'posthog-js';

type Config = {
	key: string;
} & Partial<PostHogConfig>;

export function createClient(config: Config) {
	const { key, ...settings } = config;
	const ph = posthog.init(config.key, {
		api_host: config.api_host ?? 'https://app.posthog.com',
		autocapture: {
			dom_event_allowlist: ['submit'],
		},
		ui_host: 'https://us.posthog.com',
		persistence: 'memory',
		person_profiles: 'identified_only',
		disable_surveys: true,
		disable_session_recording: true,
		capture_pageview: 'history_change',
		capture_dead_clicks: false,
		secure_cookie: true,
		// Prevent extra feature flag requests after the browser window is opened
		// This makes the client fully rely on the server-side calculated flags
		advanced_disable_feature_flags_on_first_load: true,
		...settings,
	})!;

	let consentId: string;
	let activeLaw: string;
	document.addEventListener('cookieyes_consent_update', (e) => {
		ph.capture('cookieConsent', {
			cookiesAccepted: e.detail.accepted,
			cookiesRejected: e.detail.rejected,
			cookieConsentId: consentId,
			cookieLaw: activeLaw,
		});
		ph.setPersonProperties({ cookieConsentActionComplete: true });
	});

	document.addEventListener('cookieyes_banner_load', (e) => {
		consentId = e.detail.consentId;
		activeLaw = e.detail.activeLaw;
		ph.setPersonProperties({
			cookieConsentId: e.detail.consentId,
			cookieConsentActionComplete: e.detail.isUserActionCompleted,
		});
	});

	return ph;
}

interface CookieYesEvents {
	// https://www.cookieyes.com/documentation/events-on-cookie-banner-interactions/
	cookieyes_consent_update: CustomEvent<{ accepted: Array<string>; rejected: Array<string> }>;
	// https://www.cookieyes.com/documentation/events-on-cookie-banner-load/
	cookieyes_banner_load: CustomEvent<{
		activeLaw: string;
		categories: Record<string, boolean>;
		isUserActionCompleted: boolean;
		consentId: string;
		languageCode: string;
	}>;
}
declare global {
	interface Document {
		addEventListener<K extends keyof CookieYesEvents>(
			type: K,
			listener: (this: Document, ev: CookieYesEvents[K]) => void,
		): void;
	}
}
